import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "badgeList", "pickerInput", "pickerQuantity", "badgeTemplate",
    "searchInput", "dropdown", "combobox", "singleQuantitySection",
    "singleQuantityLabel", "areaSection", "areaWidthInput",
    "areaHeightInput", "areaWidthLabel", "areaHeightLabel", "areaPreview"
  ]
  static values = {
    nextIndex: Number,
    currencyUnit: { type: String, default: "R$" },
    currencyLocale: { type: String, default: "pt-BR" },
    unitConfigs: { type: Object, default: {} },
    options: { type: Array, default: [] },
    noResultsText: { type: String, default: "Nenhum insumo encontrado" }
  }

  connect() {
    this.selectedOption = null
    this.highlightedIndex = -1
    this._boundCloseDropdown = this.closeOnOutsideClick.bind(this)
    document.addEventListener("click", this._boundCloseDropdown)
    this.renderDropdown(this.optionsValue)
    this.renderQuantityFields()
  }

  disconnect() {
    document.removeEventListener("click", this._boundCloseDropdown)
  }

  showDropdown() {
    this.filterOptions()
    this.dropdownTarget.classList.remove("hidden")
    this.searchInputTarget.setAttribute("aria-expanded", "true")
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("hidden")
    this.searchInputTarget.setAttribute("aria-expanded", "false")
    this.highlightedIndex = -1
  }

  closeOnOutsideClick(event) {
    if (!this.comboboxTarget.contains(event.target)) {
      this.hideDropdown()
    }
  }

  filterOptions() {
    const query = this.searchInputTarget.value.trim().toLowerCase()
    const filtered = query === ""
      ? this.optionsValue
      : this.optionsValue.filter(o => o.name.toLowerCase().includes(query) || (o.unit_code || "").toLowerCase().includes(query))
    this.highlightedIndex = -1
    this.renderDropdown(filtered)
  }

  renderDropdown(items) {
    const ul = this.dropdownTarget
    ul.innerHTML = ""

    if (items.length === 0) {
      const li = document.createElement("li")
      li.className = "px-3 py-2 text-slate-400 select-none"
      li.textContent = this.noResultsTextValue
      ul.appendChild(li)
      return
    }

    items.forEach((item, idx) => {
      const li = document.createElement("li")
      li.setAttribute("role", "option")
      li.setAttribute("data-index", idx)
      li.className = "flex cursor-pointer items-center justify-between px-3 py-2 hover:bg-slate-50"
      if (idx === this.highlightedIndex) li.classList.add("bg-slate-100")

      const nameSpan = document.createElement("span")
      nameSpan.className = "truncate font-medium text-slate-800"
      nameSpan.textContent = item.name

      const unitBadge = document.createElement("span")
      unitBadge.className = "ml-2 shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500"
      unitBadge.textContent = item.unit_code || "–"

      li.appendChild(nameSpan)
      li.appendChild(unitBadge)

      li.addEventListener("click", () => this.selectItem(item))
      ul.appendChild(li)
    })
  }

  selectItem(item) {
    this.selectedOption = item
    this.pickerInputTarget.value = item.id
    this.searchInputTarget.value = `${item.name} (${item.unit_code || "–"})`
    this.hideDropdown()
    this.updateQuantityConstraints()
    this.focusCurrentQuantityField()
  }

  handleKeydown(event) {
    const items = this.visibleItems()

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, items.length - 1)
        this.updateHighlight()
        break
      case "ArrowUp":
        event.preventDefault()
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
        this.updateHighlight()
        break
      case "Enter":
        event.preventDefault()
        if (this.highlightedIndex >= 0 && this.highlightedIndex < items.length) {
          this.selectItem(items[this.highlightedIndex])
        }
        break
      case "Escape":
        this.hideDropdown()
        break
    }
  }

  visibleItems() {
    const query = this.searchInputTarget.value.trim().toLowerCase()
    return query === ""
      ? this.optionsValue
      : this.optionsValue.filter(o => o.name.toLowerCase().includes(query) || (o.unit_code || "").toLowerCase().includes(query))
  }

  updateHighlight() {
    const lis = this.dropdownTarget.querySelectorAll("[role='option']")
    lis.forEach((li, i) => {
      li.classList.toggle("bg-slate-100", i === this.highlightedIndex)
    })
    if (this.highlightedIndex >= 0 && lis[this.highlightedIndex]) {
      lis[this.highlightedIndex].scrollIntoView({ block: "nearest" })
    }
  }

  updateQuantityConstraints() {
    const option = this.selectedOption
    this.pickerQuantityTarget.step = option?.step || "0.0001"
    this.pickerQuantityTarget.min = option?.min || "0.0001"
    this.renderQuantityFields()
  }

  addFromPicker(event) {
    event.preventDefault()

    const option = this.selectedOption
    const inputId = this.pickerInputTarget.value
    const quantityData = this.currentQuantityData()

    if (!inputId || !option || !quantityData) return

    const unitCode = option.unit_code || ""
    const unitCostCents = Number(option.unit_cost_cents || 0)
    const costCents = Math.round(quantityData.value * unitCostCents)

    const index = this.nextIndexValue
    const html = this.badgeTemplateTarget.innerHTML.replaceAll("NEW_INDEX", String(index))

    const wrapper = document.createElement("div")
    wrapper.innerHTML = html.trim()
    const badge = wrapper.firstElementChild

    badge.querySelector(".js-bom-input-id").value = inputId
    badge.querySelector(".js-bom-quantity").value = quantityData.submittedValue
    badge.querySelector(".js-bom-width").value = quantityData.width || ""
    badge.querySelector(".js-bom-height").value = quantityData.height || ""
    badge.querySelector(".bom-badge-label").textContent = this.badgeLabelFor(option, quantityData, unitCode)
    badge.querySelector(".bom-badge-cost").textContent = this.formatCurrency(costCents)

    this.badgeListTarget.appendChild(badge)
    this.nextIndexValue = index + 1

    this.resetPicker()
  }

  resetPicker() {
    this.selectedOption = null
    this.pickerInputTarget.value = ""
    this.searchInputTarget.value = ""
    this.pickerQuantityTarget.value = ""
    this.areaWidthInputTarget.value = ""
    this.areaHeightInputTarget.value = ""
    this.updateQuantityConstraints()
  }

  removeBadge(event) {
    event.preventDefault()
    const badge = event.target.closest("[data-bom-badge]")
    if (badge) badge.remove()
  }

  formatCurrency(cents) {
    const value = cents / 100
    const locale = this.currencyLocaleValue === "pt-BR" ? "pt-BR" : "en-US"
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)

    return `${this.currencyUnitValue} ${formatted}`
  }

  updateArea() {
    this.renderAreaPreview()
  }

  renderQuantityFields() {
    const unitCode = this.selectedOption?.unit_code || "un"
    const config = this.configFor(unitCode)
    const isAreaUnit = unitCode === "cm2"

    this.singleQuantitySectionTarget.classList.toggle("hidden", isAreaUnit)
    this.areaSectionTarget.classList.toggle("hidden", !isAreaUnit)

    if (isAreaUnit) {
      this.areaWidthLabelTarget.textContent = config.width_label || "Largura (cm)"
      this.areaHeightLabelTarget.textContent = config.height_label || "Altura (cm)"
      this.renderAreaPreview()
      return
    }

    this.singleQuantityLabelTarget.textContent = config.single_label || "Quantidade"
    this.pickerQuantityTarget.placeholder = config.placeholder || "Quantidade"
  }

  renderAreaPreview() {
    const config = this.configFor("cm2")
    const width = this.parseNumber(this.areaWidthInputTarget.value)
    const height = this.parseNumber(this.areaHeightInputTarget.value)
    const area = width > 0 && height > 0 ? width * height : 0
    const suffix = config.area_suffix || "cm2"

    this.areaPreviewTarget.textContent = `${this.formatNumber(area)} ${suffix}`
  }

  currentQuantityData() {
    const unitCode = this.selectedOption?.unit_code || ""

    if (unitCode === "cm2") {
      const width = this.parseNumber(this.areaWidthInputTarget.value)
      const height = this.parseNumber(this.areaHeightInputTarget.value)
      const area = width * height
      if (!(width > 0) || !(height > 0) || !(area > 0)) return null

      return {
        value: area,
        submittedValue: this.serializeNumber(area),
        width: this.serializeNumber(width),
        height: this.serializeNumber(height),
        displayValue: `${this.formatNumber(width)} x ${this.formatNumber(height)} cm = ${this.formatNumber(area)} cm2`
      }
    }

    const rawValue = this.pickerQuantityTarget.value.trim()
    const parsedValue = this.parseNumber(rawValue)
    if (rawValue === "" || !(parsedValue > 0)) return null

    return {
      value: parsedValue,
      submittedValue: rawValue.replace(",", "."),
      width: "",
      height: "",
      displayValue: `${rawValue} ${unitCode}`.trim()
    }
  }

  badgeLabelFor(option, quantityData, unitCode) {
    if (unitCode === "cm2") {
      return `${option.name} · ${quantityData.displayValue}`
    }

    return `${option.name} · ${quantityData.displayValue}`
  }

  focusCurrentQuantityField() {
    if ((this.selectedOption?.unit_code || "") === "cm2") {
      this.areaWidthInputTarget.focus()
      return
    }

    this.pickerQuantityTarget.focus()
  }

  configFor(unitCode) {
    return this.unitConfigsValue[unitCode] || this.unitConfigsValue.un || {}
  }

  parseNumber(value) {
    if (value == null || value === "") return 0

    const parsed = Number.parseFloat(value.toString().replace(",", "."))
    return Number.isFinite(parsed) ? parsed : 0
  }

  serializeNumber(value) {
    return value.toString()
  }

  formatNumber(value) {
    return new Intl.NumberFormat(this.currencyLocaleValue === "pt-BR" ? "pt-BR" : "en-US", {
      maximumFractionDigits: 4
    }).format(value)
  }
}
